// US federal + "fun" holiday lookups, ported from v1 scripts/app.js.
// Pure functions — no DOM, no Date global side-effects beyond what's
// supplied as arguments. Used by the v2 calendar to surface holiday
// names on the day-detail panel.

export interface HolidayResolution {
  federal?: string;
  fun?: string;
}

const FUN_HOLIDAYS: Record<string, string> = {
  '01-01': "New Year's Day",
  '02-02': 'Groundhog Day',
  '02-14': "Valentine's Day",
  '02-29': 'Leap Day',
  '03-14': 'Pi Day',
  '03-17': "St. Patrick's Day",
  '04-01': "April Fools' Day",
  '04-22': 'Earth Day',
  '05-04': 'Star Wars Day',
  '06-08': 'Best Friends Day',
  '07-04': 'Independence Day',
  '08-08': 'Cat Day',
  '09-19': 'Talk Like a Pirate Day',
  '10-04': 'World Animal Day',
  '10-31': 'Halloween',
  '11-13': 'World Kindness Day',
  '12-21': 'Winter Solstice',
  '12-24': 'Christmas Eve',
  '12-25': 'Christmas Day',
  '12-31': "New Year's Eve",
};

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  // month is 1-based here for caller clarity.
  const first = new Date(year, month - 1, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  const day = 1 + offset + (n - 1) * 7;
  return new Date(year, month - 1, day);
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  // Find the last day of the month, then walk back to the target weekday.
  const last = new Date(year, month, 0); // day 0 of next month = last of this month
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month - 1, last.getDate() - offset);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Returns the US federal holiday name for `date` if any, otherwise undefined.
 * Floating holidays (MLK day, Memorial Day, Labor Day, Thanksgiving) are
 * computed; fixed-date holidays are matched by month/day.
 */
export function getFederalHoliday(date: Date): string | undefined {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const key = `${pad(month)}-${pad(day)}`;

  // Fixed-date federal holidays.
  const fixed: Record<string, string> = {
    '01-01': "New Year's Day",
    '06-19': 'Juneteenth',
    '07-04': 'Independence Day',
    '11-11': 'Veterans Day',
    '12-25': 'Christmas Day',
  };
  if (fixed[key]) return fixed[key];

  // Floating holidays.
  // MLK Day — third Monday of January.
  if (sameDay(date, nthWeekdayOfMonth(year, 1, 1, 3))) return 'Martin Luther King Jr. Day';
  // Presidents' Day — third Monday of February.
  if (sameDay(date, nthWeekdayOfMonth(year, 2, 1, 3))) return "Presidents' Day";
  // Memorial Day — last Monday of May.
  if (sameDay(date, lastWeekdayOfMonth(year, 5, 1))) return 'Memorial Day';
  // Labor Day — first Monday of September.
  if (sameDay(date, nthWeekdayOfMonth(year, 9, 1, 1))) return 'Labor Day';
  // Columbus / Indigenous Peoples' Day — second Monday of October.
  if (sameDay(date, nthWeekdayOfMonth(year, 10, 1, 2))) return "Indigenous Peoples' Day";
  // Thanksgiving — fourth Thursday of November.
  if (sameDay(date, nthWeekdayOfMonth(year, 11, 4, 4))) return 'Thanksgiving';
  // Day after Thanksgiving — fourth Thursday of November + 1 day.
  const tg = nthWeekdayOfMonth(year, 11, 4, 4);
  const tgDayAfter = new Date(tg.getFullYear(), tg.getMonth(), tg.getDate() + 1);
  if (sameDay(date, tgDayAfter)) return 'Day after Thanksgiving';

  return undefined;
}

export function getFunHoliday(date: Date): string | undefined {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const key = `${pad(month)}-${pad(day)}`;
  return FUN_HOLIDAYS[key];
}

export function resolveHolidays(date: Date): HolidayResolution {
  const out: HolidayResolution = {};
  const federal = getFederalHoliday(date);
  if (federal) out.federal = federal;
  const fun = getFunHoliday(date);
  if (fun) out.fun = fun;
  return out;
}
